import type { SpeedrunPortalDef } from "./speedrun-levels";
import {
    drawSpeedrunPortalLine,
    speedrunPortalColor,
    speedrunPortalFramePx,
    speedrunPortalHalfThicknessPx,
} from "./speedrun-levels";

type PortalParticle = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
    size: number;
    color: string;
    tx: number;
    ty: number;
};

let portalParticles: PortalParticle[] = [];

export function resetSpeedrunPortalFx() {
    portalParticles = [];
}

function spawnPortalParticle(portal: SpeedrunPortalDef, rw: number, rh: number, color: string, ls: number) {
    const frame = speedrunPortalFramePx(portal, rw, rh);
    if (frame.len < 4) return;
    const t = 0.12 + Math.random() * 0.76;
    const tx = frame.x1 + t * frame.ux * frame.len;
    const ty = frame.y1 + t * frame.uy * frame.len;
    const spread = Math.max(14, frame.len * 0.42) * ls;
    const side = Math.random() < 0.5 ? 1 : -1;
    const offN = (0.5 + Math.random() * 1.05) * spread * side;
    const offU = (Math.random() - 0.5) * spread * 0.45;
    const x = tx + frame.nx * offN + frame.ux * offU;
    const y = ty + frame.ny * offN + frame.uy * offU;
    const life = 22 + Math.floor(Math.random() * 28);
    portalParticles.push({
        x,
        y,
        vx: 0,
        vy: 0,
        life,
        maxLife: life,
        size: (1.2 + Math.random() * 2.4) * ls,
        color,
        tx,
        ty,
    });
}

export function updateSpeedrunPortalFx(
    portals: SpeedrunPortalDef[],
    rw: number,
    rh: number,
    frameScale: number,
    lineScale: number,
) {
    if (!portals.length) {
        portalParticles = [];
        return;
    }
    for (const portal of portals) {
        const frame = speedrunPortalFramePx(portal, rw, rh);
        const spawnN = Math.max(1, Math.floor(frame.len / (42 * lineScale)));
        for (let i = 0; i < spawnN; i++) {
            if (Math.random() < 0.55) {
                spawnPortalParticle(portal, rw, rh, speedrunPortalColor(portal.group, portal.side), lineScale);
            }
        }
    }
    const fs = Math.max(0.25, frameScale);
    for (let i = portalParticles.length - 1; i >= 0; i--) {
        const p = portalParticles[i];
        const dx = p.tx - p.x;
        const dy = p.ty - p.y;
        const dist = Math.hypot(dx, dy) || 1;
        const pull = (0.11 + (1 - p.life / p.maxLife) * 0.14) * fs;
        p.vx += (dx / dist) * pull * 2.8;
        p.vy += (dy / dist) * pull * 2.8;
        const drag = Math.pow(0.9, fs);
        p.vx *= drag;
        p.vy *= drag;
        p.x += p.vx * fs;
        p.y += p.vy * fs;
        p.life -= fs * (dist < 6 ? 1.8 : 1);
        if (p.life <= 0 || dist < 2.5) portalParticles.splice(i, 1);
    }
    if (portalParticles.length > 220) portalParticles.splice(0, portalParticles.length - 220);
}

function hexToRgb(hex: string) {
    const h = hex.replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function drawPortalVortexRings(
    ctx: CanvasRenderingContext2D,
    portal: SpeedrunPortalDef,
    rw: number,
    rh: number,
    tSec: number,
    lineScale: number,
    color: string,
) {
    const frame = speedrunPortalFramePx(portal, rw, rh);
    const ht = speedrunPortalHalfThicknessPx(lineScale);
    const rgb = hexToRgb(color);
    const ringCount = 3;
    for (let r = 0; r < ringCount; r++) {
        const phase = tSec * (2.4 + r * 0.35) + r * 1.9;
        const tCenter = 0.22 + r * 0.28;
        const cx = frame.x1 + tCenter * frame.ux * frame.len;
        const cy = frame.y1 + tCenter * frame.uy * frame.len;
        const pulse = 0.85 + Math.sin(phase) * 0.15;
        const rx = ht * (2.2 + r * 0.55) * pulse;
        const ry = ht * (0.95 + r * 0.2) * pulse;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(Math.atan2(frame.uy, frame.ux) + phase * 0.35);
        ctx.strokeStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${0.22 - r * 0.04})`;
        ctx.lineWidth = Math.max(1, (2.2 - r * 0.35) * lineScale);
        ctx.setLineDash([5 * lineScale, 4 * lineScale]);
        ctx.lineDashOffset = -phase * 12 * lineScale;
        ctx.beginPath();
        ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
    }
}

function drawPortalGlowField(
    ctx: CanvasRenderingContext2D,
    portal: SpeedrunPortalDef,
    rw: number,
    rh: number,
    tSec: number,
    lineScale: number,
    color: string,
) {
    const frame = speedrunPortalFramePx(portal, rw, rh);
    const ht = speedrunPortalHalfThicknessPx(lineScale);
    const rgb = hexToRgb(color);
    const pulse = 0.72 + Math.sin(tSec * 4.2) * 0.12;
    const steps = 6;
    for (let i = 0; i < steps; i++) {
        const u = i / (steps - 1);
        const x = frame.x1 + u * frame.ux * frame.len;
        const y = frame.y1 + u * frame.uy * frame.len;
        const g = ctx.createRadialGradient(x, y, 0, x, y, ht * 3.8 * pulse);
        g.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},0.34)`);
        g.addColorStop(0.45, `rgba(${rgb.r},${rgb.g},${rgb.b},0.12)`);
        g.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, ht * 3.8 * pulse, 0, Math.PI * 2);
        ctx.fill();
    }
}

export function drawSpeedrunPortalVisual(
    ctx: CanvasRenderingContext2D,
    portal: SpeedrunPortalDef,
    rw: number,
    rh: number,
    tSec: number,
    options?: { selected?: boolean; lineScale?: number },
) {
    const ls = options?.lineScale ?? 1;
    const color = speedrunPortalColor(portal.group, portal.side);
    drawPortalGlowField(ctx, portal, rw, rh, tSec, ls, color);
    drawPortalVortexRings(ctx, portal, rw, rh, tSec, ls, color);
    drawSpeedrunPortalLine(ctx, portal, rw, rh, options);
    const frame = speedrunPortalFramePx(portal, rw, rh);
    const rgb = hexToRgb(color);
    const midX = (frame.x1 + frame.x2) * 0.5;
    const midY = (frame.y1 + frame.y2) * 0.5;
    const coreR = Math.max(3, speedrunPortalHalfThicknessPx(ls) * 0.55);
    const coreG = ctx.createRadialGradient(midX, midY, 0, midX, midY, coreR * 2.2);
    coreG.addColorStop(0, `rgba(255,255,255,0.95)`);
    coreG.addColorStop(0.35, `rgba(${rgb.r},${rgb.g},${rgb.b},0.85)`);
    coreG.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
    ctx.fillStyle = coreG;
    ctx.beginPath();
    ctx.arc(midX, midY, coreR * 2.2, 0, Math.PI * 2);
    ctx.fill();
}

/** Extra particles sucked into the portal mouth on warp. */
export function emitSpeedrunPortalAbsorbBurst(x: number, y: number, color: string, ls = 1, count = 18) {
    for (let i = 0; i < count; i++) {
        const ang = Math.random() * Math.PI * 2;
        const dist = (12 + Math.random() * 30) * ls;
        const life = 14 + Math.floor(Math.random() * 22);
        portalParticles.push({
            x: x + Math.cos(ang) * dist,
            y: y + Math.sin(ang) * dist,
            vx: 0,
            vy: 0,
            life,
            maxLife: life,
            size: (1.4 + Math.random() * 2.8) * ls,
            color,
            tx: x,
            ty: y,
        });
    }
}

export function drawSpeedrunPortalParticles(ctx: CanvasRenderingContext2D) {
    if (!portalParticles.length) return;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const p of portalParticles) {
        const t = Math.max(0, p.life / p.maxLife);
        const alpha = t * t;
        ctx.strokeStyle = p.color;
        ctx.fillStyle = p.color;
        ctx.globalAlpha = alpha * 0.85;
        const tx = p.tx - p.x;
        const ty = p.ty - p.y;
        const tl = Math.hypot(tx, ty) || 1;
        ctx.lineWidth = Math.max(0.6, p.size * 0.55);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + (tx / tl) * p.size * 2.2, p.y + (ty / tl) * p.size * 2.2);
        ctx.stroke();
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.restore();
}

export function drawSpeedrunPortals(
    ctx: CanvasRenderingContext2D,
    portals: SpeedrunPortalDef[],
    rw: number,
    rh: number,
    tSec: number,
    options?: { lineScale?: number; selectedKeys?: Set<string>; portalKey?: (i: number) => string },
) {
    const ls = options?.lineScale ?? 1;
    for (let i = 0; i < portals.length; i++) {
        const p = portals[i];
        const selected = options?.selectedKeys && options?.portalKey
            ? options.selectedKeys.has(options.portalKey(i))
            : false;
        drawSpeedrunPortalVisual(ctx, p, rw, rh, tSec, { selected, lineScale: ls });
    }
    drawSpeedrunPortalParticles(ctx);
}

/** Entry portal mouth in px (for warp burst FX). */
export function speedrunPortalEntryMouthPx(
    portal: SpeedrunPortalDef,
    rw: number,
    rh: number,
    tAlong = 0.5,
) {
    const frame = speedrunPortalFramePx(portal, rw, rh);
    const t = Math.max(0, Math.min(1, tAlong));
    return {
        x: frame.x1 + t * frame.ux * frame.len,
        y: frame.y1 + t * frame.uy * frame.len,
        nx: frame.nx,
        ny: frame.ny,
        color: speedrunPortalColor(portal.group, portal.side),
    };
}
